import { useEffect, useMemo, useState } from 'react';
import {
  Field,
  Divider,
  MessageBar,
  MessageBarBody,
  Select,
  Spinner,
  Text,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { BackLink, CostBreakdown, OrderLineRow, StickyActionFooter } from '../components';
import { useCateringService } from '../app/CateringProvider';
import { toOrderDraft } from '../state/orderDraft';
import { useOrderDraft } from '../state/OrderDraftProvider';
import type { CostCenter, MenuItem, OrderQuote } from '../types/catering';
import { ORDER_PATHS, confirmationPath, useRouter } from '../router';
import { ApiError } from '../api/httpClient';

const ORDERABLES_UNAVAILABLE_ERROR_CODE = '002';

function getSubmissionErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.errorCode === ORDERABLES_UNAVAILABLE_ERROR_CODE) {
    return 'The selected menu items are not available at the selected delivery time. Return to the menu and choose another time.';
  }

  return 'We could not submit the order. Please try again.';
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minHeight: '100%',
    paddingBottom: '5.5rem',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  lines: {
    display: 'flex',
    flexDirection: 'column',
    marginInline: `-${tokens.spacingHorizontalS}`,
  },
  footer: {
    marginTop: 'auto',
  },
});

export default function OrderReviewPage() {
  const styles = useStyles();
  const service = useCateringService();
  const { state } = useOrderDraft();
  const { navigate } = useRouter();
  const [items, setItems] = useState<MenuItem[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [quote, setQuote] = useState<OrderQuote | null>(null);
  const [costCenterId, setCostCenterId] = useState('');
  const [comment, setComment] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const draft = useMemo(() => toOrderDraft(state), [state]);
  const hasItems = state.lines.length > 0;

  useEffect(() => {
    if (!draft) {
      setError('Complete setup details before reviewing the order.');
      setIsLoading(false);
      return;
    }

    let isCurrent = true;
    setIsLoading(true);
    setError(null);
    Promise.all([service.getMenu(), service.getCostCenters(), service.quoteOrder(draft)])
      .then(([loadedItems, loadedCostCenters, loadedQuote]) => {
        if (!isCurrent) return;
        setItems(loadedItems);
        setCostCenters(loadedCostCenters);
        setQuote(loadedQuote);
      })
      .catch(() => {
        if (isCurrent) setError('We could not prepare the order review.');
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [draft, service]);

  const handleSubmit = () => {
    if (!draft || !hasItems || !quote) return;
    if (!costCenterId) {
      setValidationError('Select a cost center before submitting.');
      return;
    }

    setValidationError(null);
    setIsSubmitting(true);
    service
      .submitOrder(draft, {
        costCenterId: Number(costCenterId),
        comment: comment.trim() || undefined,
        quote,
      })
      .then((submitted) => {
        navigate(confirmationPath(submitted.retrievalCode));
      })
      .catch((error: unknown) => {
        setError(getSubmissionErrorMessage(error));
      })
      .finally(() => setIsSubmitting(false));
  };

  if (isLoading) {
    return <Spinner label="Preparing order review" />;
  }

  if (error || !draft || !hasItems || !quote) {
    return (
      <main className={styles.root}>
        <MessageBar intent="error">
          <MessageBarBody>
            {error ?? 'Add at least one menu item before reviewing the order.'}
          </MessageBarBody>
        </MessageBar>
        <BackLink onClick={() => navigate(ORDER_PATHS.menu)} />
      </main>
    );
  }

  return (
    <main className={styles.root}>
      <BackLink onClick={() => navigate(ORDER_PATHS.menu)} />
      <section className={styles.section} aria-labelledby="ordered-items-heading">
        <Text as="h2" size={300} weight="semibold" id="ordered-items-heading">
          Ordered items
        </Text>
        <div className={styles.lines}>
          {state.lines.map((line) => {
            const item = items.find((candidate) => candidate.id === line.itemId);
            return item ? (
              <div key={line.itemId}>
                <OrderLineRow
                  item={item}
                  portionsPerPerson={line.portionsPerPerson}
                  headcount={draft.setup.headcount}
                />
                <Divider />
              </div>
            ) : null;
          })}
        </div>
      </section>
      <CostBreakdown quote={quote} />
      <Field label="Cost center" validationMessage={validationError ?? undefined} required>
        <Select
          value={costCenterId}
          onChange={(event) => {
            setCostCenterId(event.target.value);
            setValidationError(null);
          }}
          aria-label="Cost center"
        >
          <option value="">Select cost center</option>
          {costCenters.map((costCenter) => (
            <option key={costCenter.id} value={String(costCenter.id)}>
              {costCenter.name} ({costCenter.code})
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Add comment">
        <Textarea value={comment} onChange={(event) => setComment(event.target.value)} />
      </Field>
      <div className={styles.footer}>
        <StickyActionFooter
          label="Submit order"
          onClick={handleSubmit}
          disabled={isSubmitting}
          loading={isSubmitting}
        />
      </div>
    </main>
  );
}
